import React, { FC, useCallback, useState } from 'react';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { UploadOutlined, UserOutlined, EditOutlined } from '@ant-design/icons';
import { Avatar, Button, Image, message, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';
import config from '../../config';
import Utils from '../../utils/Utils';
import './FileUpload.less';

// The browser uploads directly to S3 via a presigned PUT, which cannot enforce a
// size limit, so guard it on the client. Keep in sync with the backend's
// AVATAR_MAX_FILE_SIZE_BYTES (3 MB default).
const MAX_UPLOAD_SIZE_BYTES = 3 * 1024 * 1024;

type Props = {
  value?: string;
  onChange?: (value?: string) => void;
};

type CustomRequestArgs = {
  file: File | string | Blob;
  onSuccess?: (body: any, xhr?: XMLHttpRequest) => void;
  onError?: (err: Error) => void;
  onProgress?: (event: { percent: number }) => void;
};

// Reject oversized files before they are added to the upload list.
const beforeUpload = (file: File) => {
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    message.error(`File must be smaller than ${MAX_UPLOAD_SIZE_BYTES / (1024 * 1024)} MB`);
    return Upload.LIST_IGNORE;
  }
  return true;
};

// Shared onChange handler. Only propagate the stored path once the upload has
// finished; otherwise the form value would be transiently cleared to undefined
// while the file is still uploading (response.path is not yet available).
const handleUploadChange =
  (setFile: (file?: UploadFile) => void, onChange?: (value?: string) => void) =>
  ({ fileList }: UploadChangeParam) => {
    const current = fileList[0];
    if (!current) {
      setFile(undefined);
      onChange?.(undefined);
      return;
    }
    setFile(current);
    if (current.status === 'done') {
      onChange?.(current.response?.path);
    }
  };

const useS3UploadRequest = () => {
  return useCallback(async ({ file, onSuccess, onError, onProgress }: CustomRequestArgs) => {
    const blob = file as File;
    try {
      const urlResp = await fetch(`${config.api.baseUrl}/common/uploadUrl`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({ filename: blob.name, contentType: blob.type }),
      });
      if (!urlResp.ok) {
        throw new Error(`Failed to get upload URL: ${urlResp.status}`);
      }
      const { uploadUrl, path } = await urlResp.json();

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('content-type', blob.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress?.({ percent: (e.loaded / e.total) * 100 });
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`S3 upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error during S3 upload'));
        xhr.send(blob);
      });

      onSuccess?.({ path });
    } catch (err) {
      onError?.(err as Error);
    }
  }, []);
};

export const FileUpload: FC<Props> = (props) => {
  const [file, setFile] = useState<UploadFile>();
  const customRequest = useS3UploadRequest();
  return (
    <Upload
      showUploadList={false}
      maxCount={1}
      beforeUpload={beforeUpload}
      customRequest={customRequest as any}
      onChange={handleUploadChange(setFile, props.onChange)}
      fileList={file ? [file] : []}>
      <Button icon={<UploadOutlined />}>Click to Upload</Button>
    </Upload>
  );
};

export const CroppedImageUploader: FC<Props> = (props) => {
  const [file, setFile] = useState<UploadFile>();
  const customRequest = useS3UploadRequest();
  return (
    <ImgCrop>
      <Upload.Dragger
        listType={'picture'}
        showUploadList={false}
        maxCount={1}
        beforeUpload={beforeUpload}
        customRequest={customRequest as any}
        onChange={handleUploadChange(setFile, props.onChange)}
        fileList={file ? [file] : []}>
        {props.value ? <Image src={Utils.getFileUrl(props.value)} /> : 'Upload'}
      </Upload.Dragger>
    </ImgCrop>
  );
};

export const AvatarUploader: FC<Props> = (props) => {
  const [file, setFile] = useState<UploadFile>();
  const customRequest = useS3UploadRequest();
  return (
    <ImgCrop>
      <Upload.Dragger
        style={{
          background: 'transparent',
          border: 'none',
        }}
        listType={'picture'}
        showUploadList={false}
        beforeUpload={beforeUpload}
        customRequest={customRequest as any}
        onChange={handleUploadChange(setFile, props.onChange)}
        maxCount={1}
        fileList={file ? [file] : []}>
        {props.value ? (
          <>
            <Avatar className={'imageUploader'} size={160} src={Utils.getFileUrl(props.value)} />
            <div
              style={{
                position: 'absolute',
                left: '56%',
                bottom: '8%',
                backgroundColor: '#e6e6e6',
                borderRadius: '50%',
                padding: '1%',
                width: '35px',
              }}>
              <EditOutlined />
            </div>
          </>
        ) : (
          <>
            <Avatar className={'imageUploader'} size={160} icon={<UserOutlined />} />
            <div
              style={{
                position: 'absolute',
                left: '56%',
                bottom: '8%',
                backgroundColor: '#e6e6e6',
                borderRadius: '50%',
                padding: '1%',
                width: '35px',
              }}>
              <EditOutlined />
            </div>
          </>
        )}
      </Upload.Dragger>
    </ImgCrop>
  );
};
